//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { theme } from '@react/components/theme'
import {
  annualCompoundingReturn,
  annualReturnToExchangeRateCompoundingRate,
  assetsAndSharesToExchangeRate,
  dayNumberToDateString,
  erc20WeiToCommaDecString,
  exchangeRateCompounded1Year,
  exchangeRateToDecimalString,
  fromWeiToMWeiERC20NumberString,
  isNumber,
  toUTCDateString,
  weiToNumber,
} from '@shared/conversions'

import OtAdminNumberField from '@react/components/OtAdminNumberField'
import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { useOtChainTimestamp, useOtDispatch, useOtSelector } from '@react/redux/store'
import { dynamicSetCompoundingExchangeRateTx } from '@react/signedTxs/dynamicSetCompoundingExchangeRate'
import { EXCHANGE_RATE_ONE } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import { EthAddr, NumberFormatEnum } from '@shared/SchemaTypes'
import { PoolDynamicOverviewCTO } from '@shared/sharedTypes'

export default function DynamicSetCompoundingExchangeRateForm(props: {
  poolAddr: EthAddr
  accrueMode?: boolean
}): ReactElement {
  const { poolAddr, accrueMode } = props
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)
  const [isConfirmed, setIsConfirmed] = React.useState(false)
  const chainTimestamp = useOtChainTimestamp()
  const dispatch = useOtDispatch()
  const [exchangeRate, setExchangeRate] = React.useState<string>(poolCTO.exchangeRate)
  const [exchangeRateDecimalString, setExchangeRateDecimalString] = React.useState<string>(
    exchangeRateToDecimalString(poolCTO.exchangeRate)
  )

  const [assetValue, setAssetValue] = React.useState<string>(fromWeiToMWeiERC20NumberString(poolCTO.totalAssets))

  const [errorAmount, _setErrorAmount] = React.useState<string | undefined>(undefined)

  const [exchangeRateCompoundingRate, setExchangeRateCompoundingRate] = React.useState<string>(
    poolCTO.exchangeRateCompoundingRate
  )

  const [annualCompoundingRate, setAnnualCompoundingRate] = React.useState<string>(
    (annualCompoundingReturn(poolCTO.exchangeRateAtSetDay, poolCTO.exchangeRateCompoundingRate) * 100).toString()
  )

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }

  const accrueExchangerateAction = (): void => {
    setIsSubmitted(true)
    const currentAnnualCompoundingReturn = annualCompoundingReturn(
      poolCTO.exchangeRateAtSetDay,
      poolCTO.exchangeRateCompoundingRate
    )
    dispatch(
      dynamicSetCompoundingExchangeRateTx({
        poolAddr,
        exchangeRate: poolCTO.exchangeRate,
        exchangeRateCompoundingRate: poolCTO.exchangeRateCompoundingRate,
        interestRate: currentAnnualCompoundingReturn,
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const setExchangeRateAction = (): void => {
    setIsSubmitted(true)
    dispatch(
      dynamicSetCompoundingExchangeRateTx({
        poolAddr,
        exchangeRate,
        exchangeRateCompoundingRate,
        interestRate: Number(annualCompoundingRate) / 100,
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const disableButton = false

  logOT.info('ER  annualLinearReturn  ', annualCompoundingReturn)
  const exchangeRateIn1Year = exchangeRateCompounded1Year(exchangeRate, exchangeRateCompoundingRate)
  const assetsIn1Year = (BigInt(exchangeRateIn1Year) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE
  const newAssetsBI = (BigInt(exchangeRate) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE
  const setDatAssets = (BigInt(poolCTO.exchangeRateAtSetDay) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE

  if (isConfirmed) {
    return (
      <Box sx={{ marginTop: 2, flexDirection: 'column', gap: 4 }}>
        <Typography variant="h4">Confirm Change Exchange Rate</Typography>
        <Typography variant="h4"> Confirm exchangeRate {exchangeRateToDecimalString(exchangeRate)}</Typography>

        <Typography variant="h4"> Confirm annualCompoundingRate {Number(annualCompoundingRate).toFixed(4)}%</Typography>

        <Button
          disabled={isSubmitted || disableButton}
          sx={sx}
          onClick={() => {
            setExchangeRateAction()
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Set Exchange rate</Typography>
        </Button>
      </Box>
    )
  }
  return (
    <Grid container sx={{ marginTop: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            disabled={!accrueMode}
            onClick={() => {
              accrueExchangerateAction()
            }}
          >
            Accrue the Exchange Rate
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TableHeaderItem>Assets</TableHeaderItem>
        </Grid>
        <Grid item xs={3}>
          <TableHeaderItem>Shares</TableHeaderItem>
        </Grid>
        <Grid item xs={2}>
          <TableHeaderItem>Date</TableHeaderItem>
        </Grid>
        <Grid item xs={4}>
          <TableHeaderItem>Exchange Rate</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>At Last Exchange rate update</TableHeaderItem>
        </Grid>
      </Grid>

      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(setDatAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {dayNumberToDateString(poolCTO.exchangeRateSetDay)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(poolCTO.exchangeRateAtSetDay)}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Current values</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(poolCTO.totalAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateDecimalString}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Updated values</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(newAssetsBI)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateDecimalString}
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Values in 1 year</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(assetsIn1Year)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(exchangeRateIn1Year)}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <OtAdminNumberField
          id="assetValue"
          inputLabel="Updated Asset Value"
          value={assetValue}
          sx={{ width: '300px' }}
          variant="outlined"
          numberFormat={NumberFormatEnum.ERC20}
          error={!!errorAmount}
          helperText={errorAmount}
          onChange={(changedValue: string): void => {
            setAssetValue(changedValue)
            const sanitizedValue = changedValue.replace(/,/g, '')
            const assetvalueNumber = Number(sanitizedValue)

            logOT.info('assetvalueNumber Before ', assetvalueNumber, poolCTO.totalShares)

            const er = assetsAndSharesToExchangeRate(assetvalueNumber, poolCTO.totalShares)

            logOT.info('assetvalueNumber ', assetvalueNumber, changedValue, er)
            setExchangeRateDecimalString(exchangeRateToDecimalString(er))
            setExchangeRate(er.toString())
            setExchangeRateCompoundingRate(
              annualReturnToExchangeRateCompoundingRate(Number(annualCompoundingRate) / 100)
            )
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <OtAdminNumberField
          id="annualLinearReturn"
          inputLabel="Annual Linear Return"
          value={annualCompoundingRate.toString()}
          sx={{ width: '300px' }}
          variant="outlined"
          numberFormat={NumberFormatEnum.APR}
          error={!!errorAmount}
          helperText={errorAmount}
          onChange={(changedValue: string): void => {
            setAnnualCompoundingRate(changedValue)
            if (isNumber(changedValue)) {
              const alr = Number(changedValue)
              setExchangeRateCompoundingRate(annualReturnToExchangeRateCompoundingRate(alr / 100))
            }
          }}
        />
        <Box sx={{ float: 'right' }}>
          <Typography variant={'buttonBold'}>exchangeRateCompoundingRate </Typography>
          <br />
          <Typography variant={'buttonBold'}>{exchangeRateCompoundingRate} </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Button
          disabled={isSubmitted || disableButton}
          sx={sx}
          onClick={() => {
            setIsConfirmed(true)
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Confirm Exchange rate</Typography>
        </Button>
      </Grid>
      {disableButton && (
        <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
          The amount you are trying to send for withdraw is greater than the amount of {poolCTO.liquidityTokenSymbol}{' '}
          available in your vault ({weiToNumber(poolCTO?.totalAssets)}). Please deposit funds to the borrowerVault
        </Typography>
      )}
    </Grid>
  )
}
